// checkout/utils/initDeliveryLoqateLookup.ts

import { createLoqateInstance } from './loqate/createInstance';
import type { InitLoqateOptions as InitLoqateOptionsShared } from './loqate/types';

/**
 * Options for initialising Loqate.
 */
export type InitLoqateOptions = InitLoqateOptionsShared;

type LoqateScope = 'delivery' | 'billing' | 'rc';

/**
 * Initialise Loqate for delivery (stepped checkout) using the standard checkout markup.
 *
 * @param {InitLoqateOptions} opts Optional configuration
 * @returns {void}
 */
export function initDeliveryLoqateLookup(opts: InitLoqateOptions = {}): void {
	initLoqateForScope('delivery', opts);
}

/**
 * Initialise Loqate for billing (modal) using the billing modal form markup.
 *
 * @param {InitLoqateOptions} opts Optional configuration
 * @returns {void}
 */
export function initBillingLoqateLookup(opts: InitLoqateOptions = {}): void {
	initLoqateForScope('billing', opts);
}

/**
 * Initialise Loqate for Review & Confirm single-address forms
 *
 * @param {InitLoqateOptions} opts Optional configuration
 * @returns {void}
 */
export function initRcLoqateLookup(opts: InitLoqateOptions = {}): void {
	initLoqateForScope('rc', opts);
}

/**
 * Initialise Loqate for a given scope.
 *
 * @param {LoqateScope} scope Scope ("delivery" | "billing" | "rc")
 * @param {InitLoqateOptions} opts Optional configuration
 * @returns {void}
 */
function initLoqateForScope(scope: LoqateScope, opts: InitLoqateOptions): void {
	const limit = opts.limit ?? 10;

	// default behaviour:
	// - delivery => delivery behaviour
	// - billing/rc => non-delivery behaviour (billing-esque)
	const isDeliveryFlag = opts.isDelivery ?? scope === 'delivery';

	switch (scope) {
		case 'delivery':
			initDeliveryInstance({ limit, isDelivery: isDeliveryFlag });
			return;

		case 'billing':
			initBillingInstance({ limit, isDelivery: isDeliveryFlag });
			return;

		case 'rc':
			initRcInstance({ limit, isDelivery: isDeliveryFlag });
			return;

		default:
			return;
	}
}

/**
 * Delivery instance bootstrap (stepped checkout)
 *
 * @param {InitLoqateOptions} opts Required configuration (limit/isDelivery)
 * @returns {void}
 */
function initDeliveryInstance(
	opts: Required<Pick<InitLoqateOptions, 'limit' | 'isDelivery'>>,
): void {
	const form = document.getElementById('deliveryAddressForm') as HTMLFormElement | null;
	if (!form) {
		console.warn('[Loqate] Delivery: #deliveryAddressForm not found');
		return;
	}

	const searchInput = form.querySelector<HTMLInputElement>('#address-search');
	if (!searchInput) {
		console.warn('[Loqate] Delivery: #address-search not found');
		return;
	}

	const resultsContainer = form.querySelector<HTMLElement>('#address-search-results');
	const manualButton = form.querySelector<HTMLElement>('.manual-address-entry');

	const line1 = form.querySelector<HTMLInputElement>(
		'[name="deliveryAddress.address.addressLine1"]',
	);
	const line2 = form.querySelector<HTMLInputElement>(
		'[name="deliveryAddress.address.addressLine2"]',
	);
	const line3 = form.querySelector<HTMLInputElement>(
		'[name="deliveryAddress.address.addressLine3"]',
	);
	const town = form.querySelector<HTMLInputElement>('[name="deliveryAddress.address.town"]');
	const county = form.querySelector<HTMLInputElement>('[name="deliveryAddress.address.county"]');
	const postcode = form.querySelector<HTMLInputElement>(
		'[name="deliveryAddress.address.postcode"]',
	);

	if (!line1 || !town || !postcode) {
		console.warn('[Loqate] Delivery: required fields not found (line1/town/postcode)');
		return;
	}

	createLoqateInstance({
		searchInput,
		resultsContainer: resultsContainer ?? undefined,
		manualButton: manualButton ?? undefined,
		isDelivery: opts.isDelivery,
		limit: opts.limit,
		line1,
		line2: line2 ?? undefined,
		line3: line3 ?? undefined,
		town,
		county: county ?? undefined,
		postcode,
	});
}

/**
 * Billing instance
 *
 * @param {InitLoqateOptions} opts Required configuration (limit/isDelivery)
 * @returns {void}
 */
function initBillingInstance(
	opts: Required<Pick<InitLoqateOptions, 'limit' | 'isDelivery'>>,
): void {
	const form = document.querySelector<HTMLFormElement>('form[data-pca-scope="billing"]');
	if (!form) {
		console.warn("[Loqate] Billing: form[data-pca-scope='billing'] not found");
		return;
	}

	bindBillingInstanceToForm(form, opts);
}

/**
 * Bind Loqate instance to a billing form element.
 *
 * @param {HTMLFormElement} form Billing address form
 * @param {InitLoqateOptions} opts Required configuration (limit/isDelivery)
 * @returns {void}
 */
function bindBillingInstanceToForm(
	form: HTMLFormElement,
	opts: Required<Pick<InitLoqateOptions, 'limit' | 'isDelivery'>>,
): void {
	const searchInput = form.querySelector<HTMLInputElement>(
		'#address-search, [data-pca-search], [data-loqate-search="true"]',
	);
	if (!searchInput) {
		console.warn(
			'[Loqate] Billing: search input not found (#address-search / [data-pca-search] / [data-loqate-search])',
		);
		return;
	}

	const manualButton = form.querySelector<HTMLElement>('.manual-address-entry');

	const line1 = form.querySelector<HTMLInputElement>(
		'[data-pca-line1], [data-loqate-line1="true"], #address-line1',
	);
	const line2 = form.querySelector<HTMLInputElement>(
		'[data-pca-line2], [data-loqate-line2="true"], #address-line2',
	);
	const line3 = form.querySelector<HTMLInputElement>(
		'[data-pca-line3], [data-loqate-line3="true"], #address-line3',
	);
	const town = form.querySelector<HTMLInputElement>(
		'[data-pca-town], [data-loqate-town="true"], #address-town',
	);
	const county = form.querySelector<HTMLInputElement>('#address-county');
	const postcode = form.querySelector<HTMLInputElement>(
		'[data-pca-postcode], [data-loqate-postcode="true"], #address-postcode',
	);

	if (!line1 || !town || !postcode) {
		console.warn('[Loqate] Billing: required fields not found (line1/town/postcode)');
		return;
	}

	const { manualGroup, searchRow } = findBillingManualRows(form);
	patchBillingManualToggle(manualButton, manualGroup, searchRow);

	createLoqateInstance({
		searchInput,
		resultsContainer: undefined, // createInstance injects beside search input
		manualButton: manualButton ?? undefined,
		isDelivery: opts.isDelivery,
		limit: opts.limit,
		line1,
		line2: line2 ?? undefined,
		line3: line3 ?? undefined,
		town,
		county: county ?? undefined,
		postcode,
	});
}

/**
 * Review & Confirm single-address
 *
 * @param {InitLoqateOptions} opts Required configuration (limit/isDelivery)
 * @returns {void}
 */
function initRcInstance(opts: Required<Pick<InitLoqateOptions, 'limit' | 'isDelivery'>>): void {
	// The R&C snippet includes: <input type="hidden" id="pageHandle" value="singleAddress"/>
	const pageHandle = (document.getElementById('pageHandle') as HTMLInputElement | null)?.value;
	if (pageHandle !== 'singleAddress') {
		// Not the R&C singleAddress page – do nothing.
		return;
	}

	// R&C search input doesn't have data attributes; it uses the Spring-generated id.
	const searchInput =
		(document.getElementById('deliveryAddress.address.search') as HTMLInputElement | null) ??
		(document.getElementById('billingAddress.address.search') as HTMLInputElement | null);

	if (!searchInput) {
		console.warn(
			'[Loqate] R&C: search input not found (deliveryAddress.address.search / billingAddress.address.search)',
		);
		return;
	}

	const manualButton = document.querySelector<HTMLElement>(
		'#checkout-address-modal__address-form .manual-address-entry, .manual-address-entry',
	);

	// Fixed ids (#address-line1, #address-town, #address-postcode)
	const line1 = document.getElementById('address-line1') as HTMLInputElement | null;
	const line2 = document.getElementById('address-line2') as HTMLInputElement | null;
	const line3 = document.getElementById('address-line3') as HTMLInputElement | null;
	const town = document.getElementById('address-town') as HTMLInputElement | null;
	const county = document.getElementById('address-county') as HTMLInputElement | null;
	const postcode = document.getElementById('address-postcode') as HTMLInputElement | null;

	if (!line1 || !town || !postcode) {
		console.warn('[Loqate] R&C: required fields not found (line1/town/postcode)');
		return;
	}

	createLoqateInstance({
		searchInput,
		resultsContainer: undefined,
		manualButton: manualButton ?? undefined,
		isDelivery: opts.isDelivery,
		limit: opts.limit,
		line1,
		line2: line2 ?? undefined,
		line3: line3 ?? undefined,
		town,
		county: county ?? undefined,
		postcode,
	});
}

/**
 * Find the manual field group row + the search row for the billing form.
 *
 * @param {HTMLFormElement} form Billing form element
 * @returns {{ manualGroup: HTMLElement | null, searchRow: HTMLElement | null }} Row references
 */
function findBillingManualRows(form: HTMLFormElement): {
	manualGroup: HTMLElement | null;
	searchRow: HTMLElement | null;
} {
	const manualGroup = form.querySelector<HTMLElement>(
		'.manual-address-entry-group, .manual-address-fields',
	);
	const searchRow = form.querySelector<HTMLElement>(
		'.form__row--address-lookup, .manual-address-entry-wrapper',
	);
	return { manualGroup, searchRow };
}

/**
 * Make the modal "Enter address manually" control work without using jQuery.
 *
 * @param {HTMLElement | null} manualButton Manual entry trigger element
 * @param {HTMLElement | null} manualGroup Manual fields container row
 * @param {HTMLElement | null} searchRow Search row container
 * @returns {void}
 */
function patchBillingManualToggle(
	manualButton: HTMLElement | null,
	manualGroup: HTMLElement | null,
	searchRow: HTMLElement | null,
): void {
	if (!manualButton || manualButton.dataset.loqateManualBound === 'true') {
		return;
	}
	manualButton.dataset.loqateManualBound = 'true';

	manualButton.addEventListener('click', (e) => {
		e.preventDefault();

		if (searchRow) {
			searchRow.classList.add('form__row--hidden');
			searchRow.setAttribute('hidden', 'true');
		}

		if (manualGroup) {
			manualGroup.classList.remove('form__row--hidden');
			manualGroup.removeAttribute('hidden');

			const first = manualGroup.querySelector<HTMLInputElement>(
				'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
			);
			first?.focus();
		}
	});

	manualButton.addEventListener('keydown', (e: KeyboardEvent) => {
		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			manualButton.click();
		}
	});
}
